import React, { useMemo, useState } from 'react'
import { Box, Flash, useFlash } from './demoKit'

const ASSET = (name) => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  return `${base}/erp-design-miv/${name}`
}

function MivBtn({ children, variant = 'primary', onClick, type = 'button' }) {
  return (
    <button type={type} className={`miv-btn miv-btn--${variant}`} onClick={onClick}>
      {children}
    </button>
  )
}

/** Primárias MIV: verde, azul institucional, branco. */
const PRIMARY = [
  { token: '--ecossis-green', hex: '#9FC74B', uso: 'Acento / CTA / símbolo · CMYK 50,0,94,0' },
  { token: '--ecossis-blue-dark', hex: '#081E38', uso: 'Chrome, headers, wordmark · CMYK 100,85,47,57' },
  { token: '--ecossis-white', hex: '#FFFFFF', uso: 'Fundos claros · CMYK 0,0,0,0' },
]

/** Secundárias MIV: azul vibrante, cinza e preto. */
const SECONDARY = [
  { token: '--ecossis-blue', hex: '#1C47A4', uso: 'Botão primário, links, foco · CMYK 96,73,0,0' },
  { token: '--ecossis-gray', hex: '#CFCFCF', uso: 'Bordas, desabilitados · CMYK 22,16,17,0' },
  { token: '--ecossis-black', hex: '#292929', uso: 'Texto em fundo claro · CMYK 0,0,0,96' },
]

/** Complementares de grafismo (MIV págs. 34/38) — NÃO incluir cores só de setor (ex. Infra #2B72D8). */
const SUPPORT = [
  { token: '--ecossis-green-light', hex: '#D9FC6D', uso: 'Complementar grafismo' },
  { token: '--ecossis-green-dark', hex: '#98B247', uso: 'Complementar grafismo / hover verde' },
  { token: '--ecossis-blue-navy-light', hex: '#18537A', uso: 'Complementar grafismo (também Óleo e Gás)' },
  { token: '--ecossis-blue-navy-dark', hex: '#0C2D49', uso: 'Complementar grafismo (também Óleo e Gás)' },
  { token: '--ecossis-gray-light', hex: '#E0E0E0', uso: 'Complementar grafismo' },
  { token: '--ecossis-gray-dark', hex: '#B2B2B2', uso: 'Complementar grafismo' },
  { token: '--ecossis-black-light', hex: '#353535', uso: 'Par do preto (pág. 38)' },
]

const SECTORS = [
  { nome: 'Energia', hex: '#81963E', apoio: '#788739' },
  { nome: 'Infra e Saneamento', hex: '#1C47A4', apoio: '#2B72D8 / #1B448C' },
  { nome: 'Óleo e Gás', hex: '#081E38', apoio: '#18537A / #0C2D49' },
  { nome: 'Indústria e Serviços', hex: '#263850', apoio: '#38536B / #203042' },
  { nome: 'Mineração', hex: '#525B2A', apoio: '#747A3B / #494F25' },
  { nome: 'Governo', hex: '#474F5E', apoio: '— (só principal no MIV)' },
]

const LOGOS = [
  // Regra MARCA: claro = azul+verde / preto; escuro = branco / positivo / verde+branco.
  // Nunca CSS filter em PNG opaco — usar arquivo já na cor certa.
  { file: 'logo_primary_color.png', label: 'Primary color (PNG)', bg: '#fff', note: 'Fundo claro' },
  { file: 'logos-svg/logo-1h-azul-verde.svg', label: '1H azul+verde', bg: '#fff', note: 'Digital oficial claro' },
  { file: 'logos-svg/logo-2h-azul-verde.svg', label: '2H azul+verde', bg: '#fff', note: 'Com designativo' },
  { file: 'logos-svg/logo-1v-azul-verde.svg', label: '1V azul+verde', bg: '#fff', note: 'Empilhado' },
  { file: 'logos-svg/logo-1h-preto.svg', label: '1H preto (negativo)', bg: '#fff', note: 'Monocromático claro' },
  { file: 'logos-svg/type-azul.svg', label: 'Type azul', bg: '#fff', note: 'Wordmark' },
  { file: 'logos-svg/type-preto.svg', label: 'Type preto', bg: '#fff', note: 'Wordmark' },
  { file: 'logos-svg/iso-verde.svg', label: 'Iso verde', bg: '#fff', note: 'Símbolo' },
  { file: 'logos-svg/iso-azul.svg', label: 'Iso azul', bg: '#fff', note: 'Símbolo' },
  { file: 'logos-svg/iso-preto.svg', label: 'Iso preto', bg: '#fff', note: 'Símbolo' },
  { file: 'symbol_green.png', label: 'Símbolo verde PNG', bg: '#fff', note: 'Skill assets' },
  { file: 'app_icon_512.png', label: 'App icon 512', bg: '#f7f8fa', note: 'PWA / favicon' },
  { file: 'logo_white_horizontal.png', label: 'White horizontal PNG', bg: '#081E38', note: 'Fundo escuro' },
  { file: 'logos-svg/logo-1h-branco.svg', label: '1H branco (positivo)', bg: '#081E38', note: 'Pack: positivo = branco' },
  { file: 'logos-svg/logo-2h-branco.svg', label: '2H branco (positivo)', bg: '#081E38', note: 'Com designativo' },
  { file: 'logos-svg/logo-1v-branco.svg', label: '1V branco (positivo)', bg: '#081E38', note: 'Empilhado' },
  { file: 'logos-svg/logo-1h-verde-branco.svg', label: '1H verde+branco', bg: '#081E38', note: 'Símbolo verde' },
  { file: 'logos-svg/logo-2h-verde-branco.svg', label: '2H verde+branco', bg: '#081E38', note: 'Com designativo' },
  { file: 'logo_white_vertical.png', label: 'White vertical PNG', bg: '#081E38', note: 'Splash / sidebar' },
  { file: 'logo_white_symbol.png', label: 'Símbolo branco PNG', bg: '#081E38', note: 'Favicon escuro' },
  { file: 'logos-svg/iso-branco.svg', label: 'Iso branco', bg: '#081E38', note: 'Símbolo' },
  { file: 'logos-svg/type-branco.svg', label: 'Type branco', bg: '#081E38', note: 'Wordmark' },
]

function copyHex(hex, flash) {
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(hex).then(
      () => flash(`Copiado ${hex}`),
      () => flash(hex, 'warn')
    )
  } else {
    flash(hex)
  }
}

function SwatchGrid({ items, showUso }) {
  const { msg, tone, flash } = useFlash()
  return (
    <Box>
      <div className="miv-swatch-grid">
        {items.map((c) => (
          <button
            key={c.token || c.hex}
            type="button"
            className="miv-swatch"
            onClick={() => copyHex(c.hex, flash)}
            title="Copiar hex"
          >
            <span className="miv-swatch__chip" style={{ background: c.hex }} />
            <span className="miv-swatch__meta">
              <strong>{c.token || c.nome}</strong>
              <code>{c.hex}</code>
              {showUso && c.uso ? <small>{c.uso}</small> : null}
            </span>
          </button>
        ))}
      </div>
      <Flash message={msg} tone={tone} />
    </Box>
  )
}

const MOCKS = {
  mivColorsPrimary: () => (
    <Box>
      <SwatchGrid items={PRIMARY} showUso />
      <p className="miv-note">
        Primárias MIV: verde, azul e branco. Digital = HEX; impresso = CMYK (pág. 22).
      </p>
    </Box>
  ),
  mivColorsSecondary: () => (
    <Box>
      <SwatchGrid items={SECONDARY} showUso />
      <p className="miv-note">
        Secundárias MIV: azul vibrante, cinza e preto — diferenciação e suporte à identidade.
        Preferir as primárias; usar estas quando as principais já estiverem em excesso.
      </p>
    </Box>
  ),
  mivColorsSupport: () => (
    <Box>
      <SwatchGrid items={SUPPORT} showUso />
      <p className="miv-note">
        Complementares de grafismo (MIV 34/38). Cores só de setor (ex. Infra{' '}
        <code>#2B72D8</code> / <code>#1B448C</code>) ficam em <strong>Paleta por setor</strong> —
        não usar na identidade Ecossis genérica.
      </p>
    </Box>
  ),
  mivGradients: () => (
    <Box>
      <div className="miv-grad-row">
        <div
          className="miv-grad-card"
          style={{ background: 'linear-gradient(135deg, #081E38 0%, #1C47A4 100%)' }}
        >
          <span>gradient-blue (MIV pág. 41)</span>
        </div>
        <div
          className="miv-grad-card"
          style={{ background: 'linear-gradient(135deg, #1C47A4 0%, #9FC74B 100%)' }}
        >
          <span>gradient-blue-green (MIV pág. 41)</span>
        </div>
      </div>
      <p className="miv-note">Máximo 3 cores; só sobre fundo chapado ou foto. Ambos oficiais no manual.</p>
    </Box>
  ),
  mivSectors: () => (
    <Box>
      <div className="miv-sector-grid">
        {SECTORS.map((s) => (
          <div key={s.nome} className="miv-sector">
            <span className="miv-sector__bar" style={{ background: s.hex }} />
            <div>
              <strong>{s.nome}</strong>
              <code>
                {s.hex} · apoio {s.apoio}
              </code>
            </div>
          </div>
        ))}
      </div>
      <p className="miv-note">
        Identidade secundária (MIV pág. 43). Uma cor de setor por tela — não misturar com a paleta
        institucional.
      </p>
    </Box>
  ),
  mivType: () => (
    <Box>
      <div className="miv-type-sample">
        <h2 className="miv-type-sample__h">Exo bold — títulos</h2>
        <p className="miv-type-sample__p">
          Exo normal — corpo, labels e UI. Letter-spacing −0.02em (≈ −25 do manual).
        </p>
        <div className="miv-type-weights">
          <span style={{ fontFamily: 'Exo, sans-serif', fontWeight: 700 }}>Exo bold</span>
          <span style={{ fontFamily: 'Exo, sans-serif', fontWeight: 400 }}>Exo normal</span>
        </div>
      </div>
    </Box>
  ),
  mivLogos: () => (
    <Box>
      <div className="miv-logo-grid">
        {LOGOS.map((l) => (
          <figure
            key={l.file}
            className={`miv-logo-card${l.bg === '#081E38' ? ' is-dark' : ''}`}
            style={{ background: l.bg }}
          >
            <img src={ASSET(l.file)} alt={l.label} />
            <figcaption>
              <strong>{l.label}</strong>
              <span>{l.note}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Box>
  ),
  mivLogoCombos: () => {
    const [dark, setDark] = useState(false)
    return (
      <Box>
        <div className="miv-combo-toolbar">
          <MivBtn variant={dark ? 'outline' : 'primary'} onClick={() => setDark(false)}>
            Fundo claro
          </MivBtn>
          <MivBtn variant={dark ? 'primary' : 'outline'} onClick={() => setDark(true)}>
            Fundo escuro
          </MivBtn>
        </div>
        <div className={`miv-combo-stage${dark ? ' is-dark' : ''}`}>
          {dark ? (
            <>
              <img src={ASSET('logos-svg/logo-1h-branco.svg')} alt="Logo branco" />
              <img src={ASSET('logos-svg/logo-1h-verde-branco.svg')} alt="Verde + branco" />
              <img src={ASSET('logos-svg/iso-branco.svg')} alt="Iso branco" className="miv-combo-stage__sym" />
            </>
          ) : (
            <>
              <img src={ASSET('logos-svg/logo-1h-azul-verde.svg')} alt="Logo azul+verde" />
              <img src={ASSET('logos-svg/logo-1h-preto.svg')} alt="Logo preto" />
              <img src={ASSET('logos-svg/iso-verde.svg')} alt="Iso verde" className="miv-combo-stage__sym" />
            </>
          )}
        </div>
        <p className="miv-note">
          No pack MARCA: <strong>positivo = branco</strong> (fundo escuro); <strong>negativo = preto</strong>{' '}
          (fundo claro); colorido = azul+verde. Nunca colorido no escuro nem branco no claro.
        </p>
      </Box>
    )
  },
  mivLogoSafety: () => (
    <Box>
      <div className="miv-safety">
        <div className="miv-safety__box">
          <img src={ASSET('logos-svg/iso-verde.svg')} alt="" width={40} height={36} />
          <span className="miv-safety__label">40px mín.</span>
        </div>
        <p className="miv-note">
          Área de segurança ≈ altura da tipografia do wordmark. Abaixo de 40px de largura, use só o
          símbolo.
        </p>
      </div>
    </Box>
  ),
  mivGrafismos: () => (
    <Box>
      <div className="miv-graf-grid">
        <figure className="miv-graf miv-graf--asset miv-graf--on-navy">
          <img src={ASSET('grafismos/graf-3-verde-azul.svg')} alt="" />
          <figcaption>1 · Graf 3 (entrelaçado)</figcaption>
        </figure>
        <figure className="miv-graf miv-graf--asset miv-graf--on-navy">
          <img src={ASSET('grafismos/folha-verde.svg')} alt="" />
          <figcaption>2 · Folha pulverizada</figcaption>
        </figure>
        <figure className="miv-graf miv-graf--asset miv-graf--pattern-official">
          <div
            className="miv-graf__pattern-fill"
            style={{ backgroundImage: `url(${ASSET('grafismos/pattern-verde.svg')})` }}
          />
          <figcaption>3 · Pattern (opacidade baixa em chapado)</figcaption>
        </figure>
        <figure className="miv-graf miv-graf--asset">
          <img src={ASSET('grafismos/forma-ponta-retangulo.png')} alt="" />
          <figcaption>Extra · Forma ponta de retângulo</figcaption>
        </figure>
      </div>
      <p className="miv-note">Arquivos oficiais da pasta MARCA (SVG uso online). Não são aproximações CSS.</p>
    </Box>
  ),
  mivPhoto: () => {
    const [op, setOp] = useState(0.1)
    const style = useMemo(
      () => ({
        backgroundImage:
          'linear-gradient(135deg, #18537A 0%, #081E38 45%, #1C47A4 100%)',
      }),
      []
    )
    return (
      <Box>
        <div className="miv-photo" style={style}>
          <div className="miv-photo__overlay" style={{ opacity: op }} />
          <span className="miv-photo__caption">Overlay #081E38 · {(op * 100).toFixed(0)}%</span>
        </div>
        <label className="miv-photo-slider">
          Opacidade (demo 5–15% — regra MIV pág. 31)
          <input
            type="range"
            min="0.05"
            max="0.15"
            step="0.01"
            value={op}
            onChange={(e) => setOp(Number(e.target.value))}
          />
        </label>
        <p className="miv-note">
          O slider não faz parte do manual — só ilustra o intervalo oficial do filtro azul sobre a
          foto.
        </p>
      </Box>
    )
  },
  mivHierarchy: () => (
    <Box>
      <div className="miv-shell-demo">
        <header className="miv-shell-demo__top">
          <img src={ASSET('logos-svg/type-branco.svg')} alt="Ecossis" height={18} />
          <nav>
            <span className="is-on">Início</span>
            <span>Projetos</span>
            <span>RH</span>
          </nav>
        </header>
        <div className="miv-shell-demo__body">
          <aside>
            <div className="is-on">Dashboard</div>
            <div>Contratos</div>
            <div>Financeiro</div>
          </aside>
          <main>
            <h3>Conteúdo claro</h3>
            <p>Hierarquia MIV: chrome navy, primário azul, acento verde.</p>
            <div className="miv-shell-demo__actions">
              <MivBtn variant="primary">Ação primária</MivBtn>
              <MivBtn variant="accent">CTA destaque</MivBtn>
              <MivBtn variant="outline">Secundária</MivBtn>
            </div>
          </main>
        </div>
      </div>
    </Box>
  ),
  mivHierarchyMobile: () => (
    <Box>
      <div className="miv-shell-demo miv-shell-demo--mobile">
        <header className="miv-shell-demo__top">
          <img src={ASSET('logos-svg/type-branco.svg')} alt="Ecossis" height={16} />
          <span className="miv-shell-demo__menu" aria-hidden>
            ☰
          </span>
        </header>
        <div className="miv-shell-demo__body">
          <main>
            <h3>Conteúdo</h3>
            <p>Mesma hierarquia no celular: barra navy, conteúdo claro, navegação embaixo.</p>
            <div className="miv-shell-demo__actions">
              <MivBtn variant="primary">Ação primária</MivBtn>
            </div>
          </main>
          <aside>
            <div className="is-on">Início</div>
            <div>Projetos</div>
            <div>RH</div>
          </aside>
        </div>
      </div>
    </Box>
  ),
  mivDosDonts: () => (
    <Box>
      <div className="miv-dosdonts">
        <div className="miv-dos">
          <strong>Fazer</strong>
          <ul>
            <li>Logo certo para o fundo</li>
            <li>Só cores da paleta MIV</li>
            <li>Só Exo: bold nos títulos, normal no resto</li>
            <li>Texto escuro sobre verde</li>
            <li>Pattern só em fundo chapado</li>
          </ul>
        </div>
        <div className="miv-dont">
          <strong>Não fazer</strong>
          <ul>
            <li>Texto branco sobre #9FC74B</li>
            <li>Rotacionar / distorcer logo</li>
            <li>Símbolo à direita do wordmark</li>
            <li>Fundos fora da marca</li>
            <li>Gradiente com mais de 3 cores</li>
          </ul>
        </div>
      </div>
    </Box>
  ),
  mivChartGen: () => {
    const src = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/erp-design/gerador-de-graficos-ecossis.html`
    return (
      <Box className="miv-chart-gen">
        <div className="miv-chart-gen__toolbar">
          <p className="miv-note mb-0">
            Paleta Ecossis · exportável. Prefira <strong>Abrir em tela cheia</strong> para editar com folga.
          </p>
          <a className="miv-chart-gen__open" href={src} target="_blank" rel="noreferrer">
            Abrir em tela cheia
          </a>
        </div>
        <iframe
          className="miv-chart-gen__frame"
          title="Gerador de Gráficos Ecossis"
          src={src}
        />
      </Box>
    )
  },
  mivGlpiAgent: () => (
    <Box>
      <pre className="miv-note p-3 bg-light border rounded" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {`net use * \\\\10.10.201.201\\publico /user:publico Ecossis2026`}
      </pre>
      <pre className="miv-note p-3 bg-light border rounded mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {`\\\\10.10.201.201\\publico\\Instalar-GLPI-Agent.cmd`}
      </pre>
    </Box>
  ),
}

export default function MivMock({ mockId }) {
  const Comp = MOCKS[mockId]
  if (!Comp) {
    return <p className="text-muted mb-0">Mock MIV não encontrado: {mockId}</p>
  }
  return (
    <div className="miv-demo">
      <Comp />
    </div>
  )
}
